import { Reducer, useReducer, createContext, useContext } from 'react';
import { DetailType } from './SelfOthers';
import { ProfileEventType, ProfileReducerState } from './types';
import {
    getObjectiveDetailOptions,
    getSubjectiveDetailOptions,
    validations,
    SubjectiveDetail,
    ObjectiveDetail,
} from './utils';

export enum ProfileEvent {
    SetObjectiveType,
    SetObjectiveValue,
    SetSubjectiveDetails,
    SetInvestorName,
    SetDetailsType,
    SetErrors,
    SetSubmitted,
    SetAttemptsLeft,
    SetFolio,
    SetAmc,
    setInvestType,
}

export const initialState: ProfileReducerState = {
    investorName: '',
    folio: '',
    amcCode: '',
    submitted: false,
    subjective: [{ type: SubjectiveDetail.Address, value: '' }],
    objective: { type: ObjectiveDetail.PAN, value: '' },
    errors: {
        investorName: '',
        subjective: [{ value: '', type: '' }],
        objective: { value: '', type: '' },
    },
    valid: true,
    attemptsLeft: null,
    investType: DetailType.Self,
};

const getErrorsArray = (validations: Array<Function>, value: string) => {
    return validations
        .reduce((acc, validateFn) => {
            const errorMessage = validateFn.apply({}, [value]);
            return [...acc, errorMessage];
        }, [] as string[])
        .filter((error) => !!error);
};

export function validateProfile(state: ProfileReducerState): {
    valid: boolean;
    errors: ProfileReducerState['errors'];
} {
    const objectivDetailType = state.objective.type;
    const numSubjectiveDetails = state.subjective.length;
    let valid = true;
    const errors: ProfileReducerState['errors'] = {
        investorName: '',
        objective: { type: '', value: '' },
        subjective:
            state.subjective.length === 0
                ? [{ type: '', value: '' }]
                : Array.from(state.subjective).map((x) => ({
                      type: '',
                      value: '',
                  })),
    };

    if (!state.investorName) {
        errors['investorName'] = 'Investor name is required';
        valid = false;
    } else {
        errors['investorName'] = '';
    }

    if (objectivDetailType === ObjectiveDetail.None) {
        // Minimum Three Objective details
        // if (numObjectiveDetails < 3) {
        //     valid = false;
        //     throw new Error('Minimum three values must be selected');
        // }
        // Objective Validations
        for (let i = 0; i < numSubjectiveDetails; ++i) {
            const currentSubjectiveType = state.subjective[i]['type'];
            const currentSubjectiveValue = state.subjective[i]['value'];

            const currentSubjectiveValidations = validations[currentSubjectiveType] || [];
            const currentSubjectiveErrors = getErrorsArray(
                currentSubjectiveValidations,
                currentSubjectiveValue,
            );

            if (currentSubjectiveErrors.length === 0) {
                errors['subjective'][i]['value'] = '';
            } else {
                errors['subjective'][i]['value'] = currentSubjectiveErrors[0];
                valid = false;
            }
        }
        if (state.subjective.length < 3) valid = false;
    } else {
        // Objective Validation
        const objectiveType = state.objective.type;
        const objectiveValue = state.objective.value;

        if (!objectiveType) {
            errors['objective']['type'] = 'Minimum one value must be selected';
            valid = false;
        } else {
            const objectiveValidations = validations[objectiveType] || [];
            const objectiveErrors = getErrorsArray(objectiveValidations, objectiveValue);
            if (objectiveErrors.length === 0) {
                errors['objective']['value'] = '';
            } else {
                errors['objective']['value'] = objectiveErrors[0];
                valid = false;
            }
        }
        if (state.subjective.length === 0) {
            valid = false;
        } else {
            // One Subjective detail
            const subjectiveType = state.subjective[0]['type'];
            const subjectiveValue = state.subjective[0]['value'];

            const subjectiveValidations = validations[subjectiveType] || [];
            const subjectiveErrors = getErrorsArray(subjectiveValidations, subjectiveValue);

            if (subjectiveErrors.length === 0) {
                errors['subjective'][0]['value'] = '';
            } else {
                errors['subjective'][0]['value'] = subjectiveErrors[0];
                valid = false;
            }
        }
    }
    return { errors, valid };
}

export const profileReducer: Reducer<ProfileReducerState, ProfileEventType> = (state, event) => {
    switch (event.action) {
        case ProfileEvent.SetInvestorName:
            const newInvNameState = {
                ...state,
                investorName: event.payload,
            };
            return {
                ...newInvNameState,
                ...validateProfile(newInvNameState),
            };
        case ProfileEvent.SetObjectiveType:
            // remove extra items from the objective type array
            // when switching the subjective type from None to other types
            const newObjTypeState = {
                ...state,
                objective: { type: event.payload, value: '' },
                subjective:
                    event.payload === ObjectiveDetail.None
                        ? state.subjective
                        : [state.subjective[0]],
            };
            return {
                ...newObjTypeState,
                ...validateProfile(newObjTypeState),
            };
        case ProfileEvent.SetObjectiveValue:
            const newObjValueState = {
                ...state,
                objective: { ...state.objective, value: event.payload },
            };
            return {
                ...newObjValueState,
                ...validateProfile(newObjValueState),
            };
        case ProfileEvent.SetSubjectiveDetails:
            const newSubjectiveArrayState = {
                ...state,
                subjective: event.payload,
            };
            return {
                ...newSubjectiveArrayState,
                ...validateProfile(newSubjectiveArrayState),
            };
        case ProfileEvent.SetSubmitted:
            return {
                ...state,
                submitted: event.payload,
                ...validateProfile(state),
            };
        case ProfileEvent.SetDetailsType:
            const { investorName, submitted, investType, attemptsLeft } = state;
            if (event.payload === 'additionalDetails') {
                const newADState = {
                    ...initialState,
                    submitted,
                    attemptsLeft,
                    investorName,
                    investType,
                };
                return {
                    ...newADState,
                    ...validateProfile(newADState),
                };
            }
            const newMDState = {
                ...initialState,
                submitted,
                attemptsLeft,
                investorName,
                investType,
                isAdditionalDetails: false,
            };
            return {
                ...newMDState,
                ...validateProfile(newMDState),
            };
        case ProfileEvent.SetErrors:
            return {
                ...state,
                errors: event.payload,
            };
        case ProfileEvent.SetAttemptsLeft:
            return {
                ...state,
                attemptsLeft: event.payload ? Number(event.payload) : null,
            };
        case ProfileEvent.setInvestType:
            return {
                ...state,
                investType: event.payload,
            };
        case ProfileEvent.SetFolio:
            return {
                ...state,
                folio: event.payload,
            };
        case ProfileEvent.SetAmc:
            return {
                ...state,
                amcCode: event.payload,
            };
    }
    return state;
};

export const ProfileContext =
    createContext<
        { state: ProfileReducerState; dispatch: React.Dispatch<ProfileEventType> } | undefined
    >(undefined);

export const useProfileInfo = () => {
    const profileContext = useContext(ProfileContext);
    if (profileContext === undefined) {
        throw new Error('useProfileInfo must be used with in a profileInfo provider');
    }
    return profileContext;
};
